import React, { useEffect, useRef, useState } from 'react';
import {
  Segment, Header, Form, Dropdown, Input, TextArea, Button, Icon, Message,
  Grid, Card, Table, Label, Divider, Modal, List,
} from 'semantic-ui-react';
import { apiClient } from '../../apiTools/apiClient';
import '../../styles/voice-design-studio.css';

/**
 * VoiceDesignStudio — the Agency's Voice Design Studio.
 * A BC-level bench for ElevenLabs Voice Design: set broken-out levers, compose a prompt, generate 3
 * candidate voices, hear them, and save the keeper(s) — with the prompt + levers persisted so a voice
 * can be reproduced or a batch of near-identical variations saved (e.g. one per clan member).
 * The composed-prompt box is the teaching surface: levers seed it, hand-edits win.
 */

const GENDER_OPTIONS = ['male', 'female', 'neutral'].map(v => ({ key: v, value: v, text: v }));
const AGE_OPTIONS = ['child', 'young adult', 'adult', 'middle-aged', 'elderly'].map(v => ({ key: v, value: v, text: v }));
const ACCENT_OPTIONS = [
  'Neutral American', 'General American', 'British RP', 'Scottish', 'Irish', 'Australian', 'unspecified',
].map(v => ({ key: v, value: v, text: v }));
const TIMBRE_OPTIONS = [
  'warm', 'resonant', 'deep', 'bright', 'smooth', 'gravelly', 'breathy', 'raspy', 'nasal', 'crisp', 'rich', 'thin',
].map(v => ({ key: v, value: v, text: v }));
const PACE_OPTIONS = ['unhurried', 'measured', 'conversational', 'brisk'].map(v => ({ key: v, value: v, text: v }));
const LEAN_OPTIONS = ['clean narration', 'light character', 'full character'].map(v => ({ key: v, value: v, text: v }));

// Original neutral narration line (NOT book text) — what the candidates speak. >=100 chars.
const DEFAULT_SAMPLE_TEXT =
  'The evening settled over the valley, and for a long moment no one spoke. Then, with quiet certainty, ' +
  'the speaker turned to face the gathering and began to explain what would happen next, and exactly why it mattered.';

const EMPTY_LEVERS = {
  gender: 'male', age: 'adult', accent: 'Neutral American', timbre: [],
  pace: 'measured', characterLean: 'clean narration', role: '', notes: '',
};

// Assemble a clean ElevenLabs voice_description from the levers.
const composePrompt = (l) => {
  const genderWord = l.gender === 'neutral' ? 'gender-neutral' : (l.gender || '');
  const ageWord = l.age || '';
  const lead = ['A', ageWord, genderWord, 'voice'].filter(Boolean).join(' ');
  let s = lead;
  if (l.role) s += ` — ${l.role}`;
  s += '.';
  const bits = [];
  if (l.accent && l.accent !== 'unspecified') bits.push(`${l.accent} accent`);
  if (l.timbre && l.timbre.length) bits.push(`tone: ${l.timbre.join(', ')}`);
  if (l.pace) bits.push(`${l.pace} pace`);
  const leanMap = {
    'clean narration': 'clean, natural narration with no theatrics',
    'light character': 'lightly characterized but grounded',
    'full character': 'a fully characterized performance',
  };
  if (l.characterLean) bits.push(leanMap[l.characterLean] || l.characterLean);
  if (bits.length) s += ' ' + bits.map(b => b.charAt(0).toUpperCase() + b.slice(1)).join('. ') + '.';
  s += ' Suitable for long-form audiobook narration.';
  if (l.notes && l.notes.trim()) s += ` ${l.notes.trim()}`;
  return s.trim();
};

const VoiceDesignStudio = ({ litGroupUuid, onDesignsChanged }) => {
  const [levers, setLevers] = useState(EMPTY_LEVERS);
  const [prompt, setPrompt] = useState(composePrompt(EMPTY_LEVERS));
  const [sampleText, setSampleText] = useState(DEFAULT_SAMPLE_TEXT);
  const [candidates, setCandidates] = useState([]);      // [{ generatedVoiceId, audioBase64, mediaType }]
  const [candidateNames, setCandidateNames] = useState([]);
  const [savedIdx, setSavedIdx] = useState({});          // index -> true once saved
  const [generating, setGenerating] = useState(false);
  const [savingIdx, setSavingIdx] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [renamingUuid, setRenamingUuid] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const audioRef = useRef(null);

  const setLever = (k, v) => setLevers(prev => ({ ...prev, [k]: v }));

  const stopAudio = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* noop */ } audioRef.current = null; }
    setPlayingKey(null);
  };
  useEffect(() => () => stopAudio(), []);

  const play = (key, src) => {
    if (playingKey === key) { stopAudio(); return; }
    stopAudio();
    const audio = new Audio(src);
    audioRef.current = audio;
    setPlayingKey(key);
    audio.onended = () => { audioRef.current = null; setPlayingKey(null); };
    audio.onerror = () => { audioRef.current = null; setPlayingKey(null); };
    audio.play().catch(() => { audioRef.current = null; setPlayingKey(null); });
  };

  const loadDesigns = async () => {
    if (!litGroupUuid) return;
    try {
      const resp = await apiClient.get(`/litgroups/${litGroupUuid}/voice-designs`);
      setDesigns(resp.data.designs || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load saved voices');
    }
  };
  useEffect(() => {
    setCandidates([]); setSavedIdx({}); loadDesigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [litGroupUuid]);

  const compose = () => setPrompt(composePrompt(levers));

  const generate = async () => {
    setError(null); setNotice(null); stopAudio();
    if (prompt.trim().length < 20) { setError('Voice description must be at least 20 characters.'); return; }
    if (sampleText.trim().length < 100) { setError('Sample text must be at least 100 characters.'); return; }
    setGenerating(true); setCandidates([]); setSavedIdx({});
    try {
      const resp = await apiClient.post(`/litgroups/${litGroupUuid}/voice-designs/preview`, { prompt, sampleText });
      const cands = resp.data.previews || [];
      setCandidates(cands);
      const base = (levers.role && levers.role.trim()) || 'voice';
      setCandidateNames(cands.map((_, i) => `${base} · v${i + 1}`));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Voice Design failed');
    } finally {
      setGenerating(false);
    }
  };

  const saveCandidate = async (i) => {
    const c = candidates[i];
    const name = (candidateNames[i] || '').trim();
    if (!name) { setError('Give the candidate a name before saving.'); return; }
    setError(null); setNotice(null); setSavingIdx(i);
    try {
      await apiClient.post(`/litgroups/${litGroupUuid}/voice-designs`, {
        name, prompt, levers, sampleText,
        generatedVoiceId: c.generatedVoiceId, audioBase64: c.audioBase64,
      });
      setSavedIdx(prev => ({ ...prev, [i]: true }));
      setNotice(`Saved "${name}" to your voice library.`);
      loadDesigns();
      onDesignsChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSavingIdx(null);
    }
  };

  /**
   * Put the bench back to its opening state.
   *
   * ⭐ WHY IT EXISTS. Designing a cast means running the bench once per character, and each run
   * started from the previous character's levers — so a narrator's settings quietly seeded a
   * sixteen-year-old's, and the way to get a clean sheet was to reload the page.
   *
   * ⚠️ CLEARS THE CANDIDATES TOO, and that is the point rather than a side effect: three unsaved
   * previews of the PREVIOUS character sitting under a fresh prompt are the likeliest way to save
   * a voice under the wrong name. They cost nothing to regenerate.
   *
   * ⛔ Leaves `designs` alone — those are saved voices, bought and paid for, and this button clears
   * the workbench, not the library.
   */
  const clearBench = () => {
    setLevers(EMPTY_LEVERS);
    setPrompt(composePrompt(EMPTY_LEVERS));
    setSampleText(DEFAULT_SAMPLE_TEXT);
    setCandidates([]);
    setCandidateNames([]);
    setSavedIdx({});
    setError(null);
    setNotice(null);
  };

  const loadIntoBench = (d) => {
    const l = {
      gender: d.levers?.gender || 'male', age: d.levers?.age || 'adult',
      accent: d.levers?.accent || 'Neutral American', timbre: d.levers?.timbre || [],
      pace: d.levers?.pace || 'measured', characterLean: d.levers?.characterLean || 'clean narration',
      role: d.levers?.role || '', notes: d.levers?.notes || '',
    };
    setLevers(l);
    setPrompt(d.prompt || composePrompt(l));
    if (d.sampleText) setSampleText(d.sampleText);
    setNotice(`Loaded "${d.name}" into the bench — tweak the levers and generate variations.`);
  };

  const deleteDesign = async (d) => {
    setError(null);
    try {
      await apiClient.delete(`/litgroups/${litGroupUuid}/voice-designs/${d.uuid}`);
      setDesigns(prev => prev.filter(x => x.uuid !== d.uuid));
      onDesignsChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Delete failed');
    }
  };

  const startRename = (d) => { setRenamingUuid(d.uuid); setRenameValue(d.name || ''); };
  const renameDesign = async (d) => {
    const name = renameValue.trim();
    if (!name) { setRenamingUuid(null); return; }
    setError(null);
    try {
      const resp = await apiClient.put(`/litgroups/${litGroupUuid}/voice-designs/${d.uuid}`, { name });
      setDesigns(prev => prev.map(x => (x.uuid === d.uuid ? { ...x, name: resp.data.name } : x)));
      setRenamingUuid(null);
      onDesignsChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Rename failed');
    }
  };

  const promptLen = prompt.trim().length;
  const sampleLen = sampleText.trim().length;

  return (
    <div className="voice-design-studio" style={{ marginTop: '1em' }}>
      <Header as="h3">
        <Icon name="cogs" />
        <Header.Content>
          Voice Design Studio
          <Button icon basic circular size="mini" title="How this works"
            onClick={() => setTutorialOpen(true)} style={{ marginLeft: '0.6em', verticalAlign: 'middle' }}>
            <Icon name="info" />
          </Button>
          <Header.Subheader>
            Design bespoke voices from a prompt — set the levers, generate 3 candidates, save the keepers.
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}
      {notice && <Message positive onDismiss={() => setNotice(null)}>{notice}</Message>}

      <Grid stackable>
        <Grid.Row>
          {/* ---- Levers ---- */}
          <Grid.Column width={8}>
            <Segment>
              {/* ⚠️ The Clear button sits ON the Levers line, so `floated` goes on the BUTTON and the
                  Header keeps its own block — a button placed after the header would drop below it,
                  and one placed inside its content would inherit the header's type styling. */}
              <Button
                basic size="mini" floated="right" type="button"
                onClick={clearBench}
                disabled={generating || savingIdx !== null}
                title="Reset the levers, prompt and sample text to their defaults, and discard unsaved previews"
              >
                <Icon name="eraser" /> Clear
              </Button>
              <Header as="h4" content="Levers" subheader="Broken-out controls that compose the prompt" />
              <Form>
                <Form.Group widths="equal" className="vl-layer vl-l1">
                  <Form.Field>
                    <label>Gender</label>
                    <Dropdown selection fluid options={GENDER_OPTIONS} value={levers.gender}
                      onChange={(e, { value }) => setLever('gender', value)} />
                  </Form.Field>
                  <Form.Field>
                    <label>Age</label>
                    <Dropdown selection fluid options={AGE_OPTIONS} value={levers.age}
                      onChange={(e, { value }) => setLever('age', value)} />
                  </Form.Field>
                </Form.Group>
                <Form.Group widths="equal" className="vl-layer vl-l2">
                  <Form.Field>
                    <label>Accent</label>
                    <Dropdown selection fluid search allowAdditions options={ACCENT_OPTIONS} value={levers.accent}
                      additionLabel="Custom accent: "
                      onAddItem={(e, { value }) => ACCENT_OPTIONS.push({ key: value, value, text: value })}
                      onChange={(e, { value }) => setLever('accent', value)} />
                  </Form.Field>
                  <Form.Field>
                    <label>Pace</label>
                    <Dropdown selection fluid options={PACE_OPTIONS} value={levers.pace}
                      onChange={(e, { value }) => setLever('pace', value)} />
                  </Form.Field>
                </Form.Group>
                <Form.Field className="vl-layer vl-l3">
                  <label>Timbre / texture</label>
                  <Dropdown selection fluid multiple search options={TIMBRE_OPTIONS} value={levers.timbre}
                    placeholder="warm, resonant, deep…"
                    onChange={(e, { value }) => setLever('timbre', value)} />
                </Form.Field>
                <Form.Field className="vl-layer vl-l4">
                  <label>Character lean</label>
                  <Dropdown selection fluid options={LEAN_OPTIONS} value={levers.characterLean}
                    onChange={(e, { value }) => setLever('characterLean', value)} />
                </Form.Field>
                <Form.Field>
                  <label>Role / descriptor</label>
                  <Input placeholder="a strategic leader who chooses words carefully"
                    value={levers.role} onChange={(e) => setLever('role', e.target.value)} />
                </Form.Field>
                <Form.Field>
                  <label>Notes (free text, appended verbatim)</label>
                  <TextArea rows={2} placeholder="Any extra direction…"
                    value={levers.notes} onChange={(e) => setLever('notes', e.target.value)} />
                </Form.Field>
                <Button basic size="small" onClick={compose} icon labelPosition="left">
                  <Icon name="magic" /> Compose prompt from levers
                </Button>
              </Form>
            </Segment>
          </Grid.Column>

          {/* ---- Prompt + sample + generate ---- */}
          <Grid.Column width={8}>
            <Segment>
              <Header as="h4">
                Prompt sent to ElevenLabs
                <Label size="tiny" color={promptLen < 20 || promptLen > 1000 ? 'red' : 'grey'} style={{ marginLeft: '0.5em' }}>
                  {promptLen} chars
                </Label>
              </Header>
              <Form>
                <TextArea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                  style={{ fontFamily: 'inherit' }} />
                <Divider hidden />
                <Header as="h5" style={{ marginBottom: '0.35em' }}>
                  Sample line the candidates speak (original text — not from any book)
                  <Label size="tiny" color={sampleLen < 100 ? 'red' : 'grey'} style={{ marginLeft: '0.5em' }}>
                    {sampleLen} chars{sampleLen < 100 ? ` · ${100 - sampleLen} more needed` : ''}
                  </Label>
                </Header>
                <TextArea rows={3} value={sampleText} onChange={(e) => setSampleText(e.target.value)} />
              </Form>
              <Divider hidden />
              <Button primary loading={generating} disabled={generating || !litGroupUuid}
                onClick={generate} icon labelPosition="left">
                <Icon name="soundcloud" /> Generate 3 candidates
              </Button>
              <p style={{ color: '#888', marginTop: '0.5em', fontSize: '0.9em' }}>
                Each generate spends ElevenLabs credit. Saving a candidate stores it in your voice library.
              </p>
            </Segment>

            {candidates.length > 0 && (
              <Card.Group itemsPerRow={3} stackable>
                {candidates.map((c, i) => (
                  <Card key={c.generatedVoiceId || i} color={savedIdx[i] ? 'green' : undefined}>
                    <Card.Content>
                      <Card.Header style={{ fontSize: '1em' }}>
                        Candidate {i + 1} {savedIdx[i] && <Icon name="check circle" color="green" />}
                      </Card.Header>
                      <Divider hidden />
                      <Button fluid basic size="small" onClick={() => play(`cand-${i}`, `data:${c.mediaType || 'audio/mpeg'};base64,${c.audioBase64}`)}>
                        <Icon name={playingKey === `cand-${i}` ? 'stop' : 'play'} />
                        {playingKey === `cand-${i}` ? 'Stop' : 'Play'}
                      </Button>
                      <Divider hidden />
                      <Input fluid size="small" value={candidateNames[i] || ''}
                        placeholder="name this voice"
                        onChange={(e) => setCandidateNames(prev => prev.map((n, j) => (j === i ? e.target.value : n)))} />
                    </Card.Content>
                    <Card.Content extra>
                      <Button fluid positive size="small" loading={savingIdx === i} disabled={savingIdx !== null}
                        onClick={() => saveCandidate(i)}>
                        <Icon name="save" /> {savedIdx[i] ? 'Save again' : 'Save to my voices'}
                      </Button>
                    </Card.Content>
                  </Card>
                ))}
              </Card.Group>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* ---- Saved designs ---- */}
      <Segment>
        <Header as="h4" content={`My designed voices (${designs.length})`}
          subheader="Saved with their prompt + levers — load one back to spin off variations" />
        {designs.length === 0 ? (
          <p style={{ color: '#888' }}>No saved voices yet. Generate candidates above and save the ones you like.</p>
        ) : (
          <Table basic="very" compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Voice</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {designs.map(d => (
                <Table.Row key={d.uuid}>
                  <Table.Cell>
                    {renamingUuid === d.uuid ? (
                      <Input size="mini" autoFocus value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') renameDesign(d); if (e.key === 'Escape') setRenamingUuid(null); }}
                        action={{ icon: 'check', color: 'green', onClick: () => renameDesign(d) }} />
                    ) : (
                      <strong>{d.name}</strong>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {[d.levers?.gender, d.levers?.age, d.levers?.accent].filter(Boolean).join(' · ') || '—'}
                    {d.levers?.timbre?.length ? <div style={{ color: '#888', fontSize: '0.85em' }}>{d.levers.timbre.join(', ')}</div> : null}
                  </Table.Cell>
                  <Table.Cell style={{ color: '#666' }}>{d.levers?.role || '—'}</Table.Cell>
                  <Table.Cell textAlign="right">
                    {d.previewUrl && (
                      <Button icon compact basic size="small" title="Preview"
                        color={playingKey === `saved-${d.uuid}` ? 'teal' : undefined}
                        onClick={() => play(`saved-${d.uuid}`, d.previewUrl)}>
                        <Icon name={playingKey === `saved-${d.uuid}` ? 'stop' : 'headphones'} />
                      </Button>
                    )}
                    <Button icon compact basic size="small" title="Rename" onClick={() => startRename(d)}>
                      <Icon name="i cursor" />
                    </Button>
                    <Button icon compact basic size="small" title="Load into bench" onClick={() => loadIntoBench(d)}>
                      <Icon name="edit outline" />
                    </Button>
                    <Button icon compact basic size="small" title="Delete" onClick={() => deleteDesign(d)}>
                      <Icon name="trash alternate outline" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      <Modal open={tutorialOpen} onClose={() => setTutorialOpen(false)} size="small" closeIcon>
        <Modal.Header><Icon name="cogs" /> Voice Design — how it works</Modal.Header>
        <Modal.Content scrolling>
          <p>
            This bench designs a <strong>bespoke voice from a written description</strong> instead of
            picking one from a stock library — so gender and accent come out right, every time.
          </p>
          <List ordered relaxed>
            <List.Item>
              <strong>Set the levers.</strong> Gender, age, accent, timbre, pace, character-lean, and a
              free-text role. These are just building blocks for the description.
            </List.Item>
            <List.Item>
              <strong>Compose the prompt.</strong> Click <em>Compose prompt from levers</em> to turn the
              levers into a description. You can then hand-edit it freely — the text box is what actually
              gets sent, so wording matters. This is where you learn what phrasing produces what voice.
            </List.Item>
            <List.Item>
              <strong>Set the sample line.</strong> The candidates read this line aloud. It needs at least
              100 characters. Keep it original — not text from a book.
            </List.Item>
            <List.Item>
              <strong>Generate 3 candidates</strong> and listen. Each generate spends a little ElevenLabs
              credit.
            </List.Item>
            <List.Item>
              <strong>Save the keeper(s).</strong> Name a candidate and hit <em>Save to my voices</em> —
              it's stored in your voice library and here with its prompt + levers. You can save more than
              one from a single batch (handy for a group of characters who should sound almost the same).
            </List.Item>
          </List>
          <Divider />
          <Header as="h5" content="Tips" />
          <List bulleted>
            <List.Item>Wrong gender or accent? Say it explicitly in the prompt — e.g. "a female voice, neutral American accent."</List.Item>
            <List.Item>Too theatrical? Add "understated, no theatrics." Too flat? Try "warm, expressive."</List.Item>
            <List.Item>Found a voice you like? Use <em>Load into bench</em> on a saved row, tweak one lever, and generate variations.</List.Item>
            <List.Item>Rename any saved voice with the cursor icon; changes save immediately.</List.Item>
          </List>
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={() => setTutorialOpen(false)}>Got it</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default VoiceDesignStudio;
